import { connectSession } from "../handlers/session";
import { updateCallAndSend, updateConferenceAndSend } from "../handlers/call";
import { changeAgentStateToAvailable } from "../handlers/agent";
import {
  API_CALL_ANSWER,
  API_CALL_DIAL,
  API_CALL_HOLD,
  API_CALL_MUTE,
  API_CALL_PLAY,
  API_CALL_REJECT,
  API_CALL_SEND_DTMF,
  CONTACT_EVENT_STATE_MAP,
  LOG_PREFIX,
} from "../../constants/constants";
import { sendErrorNotification } from "../../utils";
import Emitter from "../../emitter";
import {
  API_CALL_ANSWER_ERROR,
  API_CALL_DIAL_ERROR,
  API_CALL_HANGUP,
  API_CALL_HOLD_OR_RESUME_AGENT_CONNECTION,
  API_CALL_TRANSFER,
} from "../constants";
import {
  TELEMETRY_INFO,
  TELEMETRY_ERROR,
  ATTACH_CONTACT_TO_CALLBACK,
  UPDATE_CALLBACK_STATUS,
} from "../../constants/events";
import { showErrorToast } from "../../utils/utils";
import { DIAL_CALL_IN_PROGRESS } from "../../constants/action-types";
import storeRTK from "../../store/storeRTK";
import {
  API_CALL_ADD_CONNECTION,
  API_CALL_ADD_QUEUE_CONNECTION,
  API_CALL_MERGE,
  API_CALL_CONFERENCE_HOLD,
  API_CALL_CONFERENCE_PLAY,
  API_CALL_CONFERENCE_PARTICIPANT_MUTE,
  API_CALL_REMOVE_CONNECTION,
  API_CALL_DISCONNECT_AGENT,
  API_AGENT_GLOBAL_MUTE,
} from "../../constants/constants";
import { API_CALL_BARGE } from "../constants";
import { resetConference } from "../../actions/call";
import { setAgentGlobalMute } from "../../actions/session";
import { addAttributesToCall } from "../../api/session-api";
const lp = LOG_PREFIX + "[api.call.ts]: ";

// Reusable utility function to get third party connections
function getThirdPartyConnections(contact: any, logPrefix: string = lp) {
  try {
    // Try native method first
    if (typeof (contact as any).getThirdPartyConnections === "function") {
      return (contact as any).getThirdPartyConnections();
    }

    // Fallback to filtering all connections
    if (typeof (contact as any).getConnections === "function") {
      const all = (contact as any).getConnections();
      const initial = (contact as any).getActiveInitialConnection?.();
      return all.filter((conn: any) => {
        try {
          const type = conn.getType && conn.getType();
          return String(type).toLowerCase() !== "agent" && conn !== initial;
        } catch (e) {
          return false;
        }
      });
    }
  } catch (e) {
    console.log(logPrefix + "Error getting third party connections", e);
  }
  return [];
}

// Reusable utility function to validate active contact
function validateActiveContact(isCall: boolean = true, logPrefix: string = lp) {
  const contact = getAgentContactById(isCall);
  if (!contact) {
    console.log(logPrefix + "No active call found");
    return null;
  }
  return contact;
}

// Reusable utility function to emit telemetry error
function emitTelemetryError(
  type: string,
  error: any,
  info: string,
  additionalData: any = {}
) {
  Emitter.emit(TELEMETRY_ERROR, {
    type: "notif",
    chan: TELEMETRY_ERROR,
    body: {
      type,
      error,
      info,
      ...additionalData,
    },
  });
}

// Reusable utility function to emit telemetry info
function emitTelemetryInfo(
  type: string,
  info: string,
  message: string,
  additionalData: any = {}
) {
  Emitter.emit(TELEMETRY_ERROR, {
    type: "notif",
    chan: TELEMETRY_INFO,
    body: {
      type,
      info,
      message,
      ...additionalData,
    },
  });
}

export function getAgentContactById(isCall: boolean, id?: any) {
  let contacts;
  if (!!isCall) {
    contacts = connectSession.agent.getContacts(connect.ContactType.VOICE);
    if (contacts.length) return contacts[0];
  } else {
    contacts = connectSession.agent.getContacts();
    if (contacts.length) {
      const contact = contacts.find((ct: any) => ct.contactId === id);
      return contact;
    }
  }
  return null;
}

export function acceptCallContact(ev: any) {
  const logPrefix =
    lp + "acceptCallContact contactId: " + ev.body.conversationId;
  try {
    getAgentContactById(true).accept({
      success: function () {
        console.log(logPrefix + "Accepted via Streams with contact id: ");
        Emitter.emit(TELEMETRY_ERROR, {
          type: "notif",
          chan: TELEMETRY_INFO,
          body: {
            type: API_CALL_ANSWER,
            info: logPrefix + "Accepted via Streams with contact id: ",
          },
        });
      },
      failure: function (error: any) {
        const errorMessage = JSON.parse(error);
        if (errorMessage && errorMessage?.message) {
          console.log(
            logPrefix + "Failed to establish connection via Streams",
            `${errorMessage?.message}`
          );
          sendErrorNotification(
            "Failed to establish connection via Streams",
            `${errorMessage?.message}`
          );
        }
        console.log(
          logPrefix + "Failed to establish connection via Streams with error",
          error
        );
        Emitter.emit(TELEMETRY_ERROR, {
          type: "notif",
          chan: TELEMETRY_ERROR,
          body: {
            type: API_CALL_ANSWER_ERROR,
            error: errorMessage,
            info: logPrefix + "Failed to establish connection via Streams",
          },
        });

        updateCallAndSend(CONTACT_EVENT_STATE_MAP.ENDED, {
          contactId: ev.body.conversationId,
        });
      },
    });
  } catch (e) {
    console.log(logPrefix + "Error in accepting call with error", e);
    updateCallAndSend(CONTACT_EVENT_STATE_MAP.ENDED, {
      contactId: ev.body.conversationId,
    });
  }
}

export function disconnectCallContactOnRefreshError(ev: any) {
  try {
    // Streams API call to Drop a Connected Contact
    const callContact = getAgentContactById(true);
    if (callContact) {
      getAgentContactById(true)
        .getAgentConnection()
        .destroy({
          success: function () {
            console.log("Contact disconnected via Streams");
            Emitter.emit(TELEMETRY_ERROR, {
              type: "notif",
              chan: TELEMETRY_ERROR,
              body: {
                type: API_CALL_ANSWER,
                message:
                  lp + " Contact disconnected via Streams due to Refresh Error",
                disconnectReason: "Contact Refresh Error",
              },
            });
            clearCurrentCallContact();
          },
          failure: function (err: any) {
            clearCurrentCallContact();
            console.log("Failed to disconnect the contact via Streams", err);
            if (err.type === "NetworkingError") {
              // If the error is due to network error, we can safely assume that the call will be disconnected when network is back
            } else {
              updateCallAndSend(CONTACT_EVENT_STATE_MAP.ENDED, {
                contactId: ev.body.conversationId,
              });
            }
          },
        });
    } else {
      clearCurrentCallContact();
      updateCallAndSend(CONTACT_EVENT_STATE_MAP.ENDED, {
        contactId: ev.body.conversationId,
      });
    }
  } catch (error) {
    console.log("Error in disconnect call contact on CCP, removing from AIC");
    updateCallAndSend(CONTACT_EVENT_STATE_MAP.ENDED, {
      contactId: ev.body.conversationId,
    });
  }
}

export function disconnectCallContact(ev: any) {
  const logPrefix =
    lp + "disconnectCallContact contactId: " + ev.body.conversationId;
  try {
    // Streams API call to Drop a Connected Contact
    const callContact = getAgentContactById(true);
    if (callContact) {
      getAgentContactById(true)
        .getAgentConnection()
        .destroy({
          success: function () {
            console.log(logPrefix + "Contact disconnected via Streams");
            Emitter.emit(TELEMETRY_ERROR, {
              type: "notif",
              chan: TELEMETRY_INFO,
              body: {
                type: API_CALL_HANGUP,
                message: logPrefix + " Contact disconnected via Streams",
                disconnectReason: "Agent Disconnect",
              },
            });
          },
          failure: function (err: any) {
            clearCurrentCallContact();
            console.log(
              logPrefix + "Failed to disconnect the contact via Streams",
              err,
              JSON.parse(err)
            );
            Emitter.emit(TELEMETRY_ERROR, {
              type: "notif",
              chan: TELEMETRY_ERROR,
              body: {
                type: API_CALL_HANGUP,
                message: logPrefix + " Contact disconnected via Streams",
                error: err,
              },
            });
            let errorObj: { type: string; message: string } | null = null;
            try {
              errorObj = JSON.parse(err);
            } catch (e) {
              console.log("Error in parsing error object", e);
            }
            if (errorObj && errorObj.type === "NetworkingError") {
              console.log(
                logPrefix +
                "Call is not disconnected due to Network Error, will try when network is back",
                "Call is not disconnected due to Network Error, will try when network is back",
                errorObj
              );
              // If the error is due to network error, we can safely assume that the call will be disconnected when network is back
            } else {
              updateCallAndSend(CONTACT_EVENT_STATE_MAP.ENDED, {
                contactId: ev.body.conversationId,
              });
            }
          },
        });
    } else {
      console.log(logPrefix + "No call contact found with contactId");
      clearCurrentCallContact();
      updateCallAndSend(CONTACT_EVENT_STATE_MAP.ENDED, {
        contactId: ev.body.conversationId,
      });
    }
  } catch (error) {
    console.log(
      `${logPrefix} Error in disconnect call contact on CCP, removing from AIC`,
      error
    );
    updateCallAndSend(CONTACT_EVENT_STATE_MAP.ENDED, {
      contactId: ev.body.conversationId,
    });
  }
}

export function updateCall(ev: any) {
  const logPrefix =
    lp + "updateCall contactId: " + ev.body.conversationId || ev.body.contactId;
  const contact = getAgentContactById(true);
  const callConnection = contact.getActiveInitialConnection();
  if (ev.body?.held === true) {
    callConnection.hold({
      success: function () {
        console.log(logPrefix + "Successfully hold call");
      },
      failure: function (error: any) {
        console.log(logPrefix + "Error during hold call", error);
        Emitter.emit(TELEMETRY_ERROR, {
          type: "notif",
          chan: TELEMETRY_ERROR,
          body: {
            type: API_CALL_HOLD,
            message: logPrefix + "Error during hold call",
            disconnectReason: error,
          },
        });
      },
    });
  } else if (ev.body?.held === false) {
    callConnection.resume({
      success: function () {
        console.log("Successfully resumed call");
      },
      failure: function (error: any) {
        console.log(logPrefix + "Error during resume call", error);
        Emitter.emit(TELEMETRY_ERROR, {
          type: "notif",
          chan: TELEMETRY_ERROR,
          body: {
            type: API_CALL_HOLD,
            message: logPrefix + "Error in resuming call",
            disconnectReason: error,
          },
        });
      },
    });
  }
}

export function rejectCallContact(ev: any) {
  const logPrefix =
    lp + "rejectCallContact contactId: " + ev.body.conversationId;
  changeAgentStateToAvailable();
  getAgentContactById(true).reject({
    success: function () {
      console.log(logPrefix + "Rejected Incoming call via Streams");
      Emitter.emit(TELEMETRY_ERROR, {
        type: "notif",
        chan: TELEMETRY_INFO,
        body: {
          type: API_CALL_REJECT,
          message: logPrefix + "Contact rejected via Streams",
          disconnectReason: ev.body?.disconnectReason
            ? ev.body?.disconnectReason
            : "Agent Reject",
        },
      });
      clearCurrentCallContact();
    },
    failure: function (err: any) {
      console.log(logPrefix + "Failed to Reject call via Streams", err);
      clearCurrentCallContact();
      updateCallAndSend(CONTACT_EVENT_STATE_MAP.ENDED, {
        contactId: ev?.body?.conversationId,
      });
    },
  });
}
export function clearCurrentCallContact() {
  const contact = getAgentContactById(true);
  if (contact) {
    try {
      contact.clear();
    } catch (e) { }
  }
}

// Escalate monitoring from SILENT_MONITOR to BARGE for the supervisor leg
// internal: get monitoring contact
// function getMonitoringContact(): any | null {
//   try {
//     const contacts =
//       connectSession.agent.getContacts(connect.ContactType.VOICE) || [];
//     for (const ct of contacts) {
//       try {
//         if (ct?.getAgentConnection?.()?.isSilentMonitor?.() === true) return ct;
//       } catch { }
//     }
//   } catch { }
//   return null;
// }

// Retry until monitoring leg is connected, then attempt BARGE
// function requestBargeWhenReady(
//   attempts = 10,
//   delayMs = 300,
//   shouldMute = false,
//   conversationId?: string
// ) {
//   const logPrefix = lp + "escalateMonitorToBarge";
//   const mon = getMonitoringContact();
//   if (!mon) {
//     if (attempts > 0)
//       return setTimeout(
//         () =>
//           requestBargeWhenReady(
//             attempts - 1,
//             delayMs,
//             shouldMute,
//             conversationId
//           ),
//         delayMs
//       );
//     console.log(
//       logPrefix +
//       ": No monitoring leg found (isSilentMonitor=false). Ensure MonitorContact included BARGE."
//     );
//     return;
//   }
//   const stateType = mon?.getStatus?.().type;
//   if (stateType !== "connected") {
//     if (attempts > 0)
//       return setTimeout(
//         () =>
//           requestBargeWhenReady(
//             attempts - 1,
//             delayMs,
//             shouldMute,
//             conversationId
//           ),
//         delayMs
//       );
//     console.log(
//       logPrefix + ": Monitoring leg not connected; state=",
//       stateType
//     );
//     return;
//   }
//   if (typeof (mon as any).updateMonitorParticipantState !== "function") {
//     console.log(
//       logPrefix +
//       ": updateMonitorParticipantState not available on monitoring contact"
//     );
//     return;
//   }
//   (mon as any).updateMonitorParticipantState(
//     (connect as any).MonitoringMode.BARGE,
//     {
//       success: function () {
//         console.log(logPrefix + ": Barge requested successfully");

//         // Apply mute after successful barge-in if requested
//         if (shouldMute && conversationId) {
//           console.log(logPrefix + ": Applying mute after successful barge-in");
//           setTimeout(() => {
//             connectSession.agent.mute();
//             updateCallAndSend(
//               CONTACT_EVENT_STATE_MAP.MUTETOGGLE,
//               { contactId: conversationId },
//               { muted: true }
//             );
//             // Also update conference call state to ensure second call shows correct mute state
//             updateConferenceAndSend(
//               CONTACT_EVENT_STATE_MAP.MUTETOGGLE,
//               { contactId: conversationId },
//               { muted: true }
//             );
//           }, 500); // Small delay to ensure barge state is established
//         }
//       },
//       failure: function (err: any) {
//         console.log(logPrefix + ": Barge request failed", err);
//       },
//     }
//   );
// }

let counter = 0;

/**
 * Initiates a barge-in operation for supervisor monitoring
 * On successful barge-in, adds a barge-in attribute to the call for tracking
 */
function startBargeIn() {
  let contact = null;
  
  // Try to get contact with error handling
  try {
    contact = getAgentContactById(true);
  } catch (error) {
    console.log("startBargeIn: Error getting contact:", error);
    return;
  }
  
  // Check max attempts BEFORE incrementing
  if (counter >= 10) {
    console.log("Max barge attempts (10) reached, giving up");
    counter = 0; // Reset for future attempts
    return;
  }
  
  if (contact) {
    counter += 1; // Only increment once per attempt
    console.log(`Barge attempt ${counter} of 10`);

    //TODO: Add telemetry for barge-in
    Emitter.emit(TELEMETRY_INFO, {
      type: "notif",
      chan: TELEMETRY_INFO,
      body: {
        type: API_CALL_BARGE,
        message: `Barge attempt for conversationId: ${contact.getContactId()} barge attempt ${counter} of 10`,
      },
    });

    try {
      contact.bargeIn({
        success: function () {
          console.log("Barge-in successful");
          counter = 0; // Reset on success
          
          // Add barge-in attribute to call for tracking purposes
          // This helps identify calls where a supervisor has barged in
          // for compliance, quality assurance, and supervisory reporting
          const contactId = contact.getContactId();
          if (contactId) {
            addAttributesToCall(contactId, { AIC_IS_BARGE: true })
              .then(() => {
                console.log(
                  "startBargeIn: Successfully added barge-in attribute to call"
                );
                Emitter.emit(TELEMETRY_INFO, {
                  type: "notif",
                  chan: TELEMETRY_INFO,
                  body: {
                    type: API_CALL_BARGE,
                    message: `Barge attempt for conversationId: ${contact.getContactId()} barge attempt ${counter} of 10`,
                    success: true,
                  },
                });
              })
              .catch((error) => {
                Emitter.emit(TELEMETRY_ERROR, {
                  type: "notif",
                  chan: TELEMETRY_ERROR,
                  body: {
                    type: API_CALL_BARGE,
                    message: `Barge attempt for conversationId: ${contact.getContactId()} barge attempt ${counter} of 10`,
                    success: false,
                    error: error,
                  },
                });
                console.error(
                  "startBargeIn: Failed to add barge-in attribute to call:",
                  error
                );
              });
          }
        },
        failure: function (err: any) {
          console.log(`Barge attempt ${counter} failed:`, err);
          
          if (counter >= 10) {
            console.log("Max barge attempts reached, stopping retries");
            counter = 0; // Reset for future attempts
          } else {
            console.log(`Retrying barge-in (attempt ${counter + 1} of 10)`);
            setTimeout(() => {
              startBargeIn(); // Retry with delay
            }, 1000); // Add delay between retries
          }
        },
      });
    } catch (error) {
      console.log(`startBargeIn: Error calling bargeIn:`, error);
      // Retry after error
      if (counter < 10) {
        setTimeout(() => {
          startBargeIn();
        }, 1000);
      }
    }
  } else {
    console.log("No contact found for barge-in");
    // Retry if no contact found (might still be initializing)
    if (counter < 10) {
      setTimeout(() => {
        startBargeIn();
      }, 1000);
    }
  }
}

function escalateMonitorToBarge(ev?: any) {
  const shouldMute = ev?.body?.mute || false;
  const conversationId = ev?.body?.conversationId;
  console.log(
    `${lp}escalateMonitorToBarge: shouldMute=${shouldMute}, conversationId=${conversationId}`
  );

  // Start waiting for connected state with retry logic
  waitForConnectedState(0, shouldMute, conversationId);
}

function waitForConnectedState(attempts = 0, shouldMute = false, conversationId: string, maxAttempts = 20, delayMs = 500) {
  const logPrefix = lp + "waitForConnectedState";
  let contact = null;
  
  // Try to get contact with error handling
  try {
    contact = getAgentContactById(true);
  } catch (error) {
    console.log(`${logPrefix}: Error getting contact (attempt ${attempts + 1}/${maxAttempts}):`, error);
  }

  if (!contact) {
    // Check if we've reached max attempts
    if (attempts >= maxAttempts - 1) {
      console.log(`${logPrefix}: Max attempts (${maxAttempts}) reached. Could not get monitoring contact.`);
      return;
    }

    console.log(`${logPrefix}: No contact found, retrying in ${delayMs}ms (attempt ${attempts + 1}/${maxAttempts})`);
    setTimeout(() => {
      waitForConnectedState(attempts + 1, shouldMute, conversationId, maxAttempts, delayMs);
    }, delayMs);
    return;
  }

  let stateType = "unknown";
  try {
    stateType = contact?.getStatus?.().type;
  } catch (error) {
    console.log(`${logPrefix}: Error getting contact status:`, error);
  }

  console.log(`${logPrefix}: Attempt ${attempts + 1}/${maxAttempts} - Contact state is ${stateType}`);

  if (stateType === "connected") {
    console.log(`${logPrefix}: Contact is now connected, proceeding with barge`);
    setTimeout(() => {
      startBargeIn();
    }, 500);
    return;
  }

  // Check if we've reached max attempts
  if (attempts >= maxAttempts - 1) {
    console.log(`${logPrefix}: Max attempts (${maxAttempts}) reached. Contact never reached connected state. Final state: ${stateType}`);
    return;
  }

  // Continue waiting if in transitional states
  if (stateType === "connecting" || stateType === "ringing" || stateType === "dialing" || stateType === "unknown") {
    console.log(`${logPrefix}: Contact in transitional state (${stateType}), waiting for connected...`);
    setTimeout(() => {
      waitForConnectedState(attempts + 1, shouldMute, conversationId, maxAttempts, delayMs);
    }, delayMs);
  } else {
    // Unexpected state - stop trying
    console.log(`${logPrefix}: Contact in unexpected state (${stateType}), cannot proceed with barge`);
  }
}

export function initOutBoundCall(ev: any) {
  const logPrefix = lp + "initOutBoundCall " + ev.phoneNumber;
  var endpoint = connect.Endpoint.byPhoneNumber(ev.phoneNumber);
  connectSession.agent.connect(endpoint, {
    success: function (res: any) {
      console.log(
        logPrefix + "Outbound call successfull to the endpoint",
        endpoint,
        res
      );
      Emitter.emit(TELEMETRY_ERROR, {
        type: "notif",
        chan: TELEMETRY_INFO,
        body: {
          type: API_CALL_DIAL,
          message: `${logPrefix} Outbound call successfull to the endpoint ${endpoint}`,
        },
      });
      if (ev.isAttachAttributes) {
        setTimeout(() => Emitter.emit(ATTACH_CONTACT_TO_CALLBACK), 1000);
      }
    },
    failure: function (error: any) {
      const errorMessage = JSON.parse(error);
      if (errorMessage && errorMessage?.message) {
        console.log(
          logPrefix + "Failed to connect outbound call",
          `${errorMessage?.message}`
        );
        sendErrorNotification(
          logPrefix + "Failed to connect outbound call",
          `${errorMessage?.message}`
        );
      }
      Emitter.emit(TELEMETRY_ERROR, {
        type: "notif",
        chan: TELEMETRY_ERROR,
        body: {
          type: API_CALL_DIAL_ERROR,
          error: errorMessage,
          message: `${logPrefix} Failed to connect outbound call ${endpoint}`,
        },
      });
      Emitter.emit(DIAL_CALL_IN_PROGRESS);
      showErrorToast(
        "Invalid number, Failed to connect outbound call",
        "callDialError"
      );
      console.log(logPrefix + "Failed to connect outbound call: ", error);
      if (ev.isAttachAttributes) {
        Emitter.emit(UPDATE_CALLBACK_STATUS, {
          body: {
            contactId: ev.conversationId,
            phoneNumber: ev.phoneNumber,
            status: "FAILED",
            message: "Invalid Number",
          },
        });
      }
    },
  });
}

export function addConnectionToCall(ev: any) {
  const logPrefix = lp + "addConnectionToCall " + ev.phoneNumber;
  const endpoint = connect.Endpoint.byPhoneNumber(ev.phoneNumber);
  const contact = getAgentContactById(true);

  if (!contact) {
    console.log(logPrefix + " No active call to add connection to");
    return;
  }

  contact.addParticipant(endpoint, {
    success: function (res: any) {
      console.log(logPrefix + " addConnection success", res);
      updateConferenceAndSend("CONNECTED", contact, {
        muted: contact.getAgentConnection()?.isMute(),
      });
      Emitter.emit(TELEMETRY_ERROR, {
        type: "notif",
        chan: TELEMETRY_INFO,
        body: {
          type: API_CALL_ADD_CONNECTION,
          info:
            logPrefix + " Connection added successfully to " + ev.phoneNumber,
          message: logPrefix + " addConnection success",
          phoneNumber: ev.phoneNumber,
        },
      });
    },
    failure: function (error: any) {
      console.log(logPrefix + " addConnection failed", error);
      Emitter.emit(TELEMETRY_ERROR, {
        type: "notif",
        chan: TELEMETRY_ERROR,
        body: {
          type: API_CALL_ADD_CONNECTION,
          error: error,
          info: logPrefix + " Failed to add connection to " + ev.phoneNumber,
          phoneNumber: ev.phoneNumber,
        },
      });
    },
  });
}

export function addQueueConnectionToCall(ev: any) {
  const logPrefix = lp + "addQueueConnectionToCall " + ev.queueName;
  if (!ev?.transferEndpoint) {
    console.log(logPrefix + " No queue endpoint provided");
    return;
  }

  // Build queue endpoint object
  const queueEndpoint = {
    agentLogin: null,
    endpointId: ev.transferEndpoint.arn,
    endpointARN: ev.transferEndpoint.arn,
    phoneNumber: null,
    name: ev.transferEndpoint.name,
    queue: null,
    type: ev.transferEndpoint.quick_connect_type.toLowerCase(),
  } as unknown as connect.EndpointType.QUEUE;

  // Validate active primary call to add queue connection
  const contact = validateActiveContact(true, logPrefix);
  if (!contact) {
    return;
  }

  contact.addParticipant(queueEndpoint, {
    success: function (res: any) {
      console.log(logPrefix + " addQueueConnection success", res);
      updateConferenceAndSend("CONNECTED", contact, {
        muted: contact.getAgentConnection()?.isMute(),
      });
      emitTelemetryInfo(
        API_CALL_ADD_QUEUE_CONNECTION,
        logPrefix + " Queue connection added successfully to " + ev.queue,
        logPrefix + " addQueueConnection success",
        { queue: ev.queue }
      );
    },
    failure: function (error: any) {
      console.log(logPrefix + " addQueueConnection failed", error);
      emitTelemetryError(
        API_CALL_ADD_QUEUE_CONNECTION,
        error,
        logPrefix + " Failed to add queue connection to " + ev.queue,
        { queue: ev.queue }
      );
    },
  });
}

/**
 * Merges active call connections into a conference call
 * On successful merge, adds a conference attribute to the call for tracking
 */
export function mergeConnections() {
  const logPrefix = lp + "mergeConnections ";
  // Validate active call early to merge connections
  const contact = getAgentContactById(true);
  if (!contact) {
    console.log(logPrefix + " No active call to merge");
    return;
  }
  try {
    contact.conferenceConnections({
      success: function () {
        console.log(
          logPrefix + " Connections merged successfully into conference"
        );
        Emitter.emit(TELEMETRY_ERROR, {
          type: "notif",
          chan: TELEMETRY_INFO,
          body: {
            type: API_CALL_MERGE,
            info:
              logPrefix + " Connections merged successfully into conference",
            message: logPrefix + " mergeConnections success",
          },
        });
      },
      failure: function (error: any) {
        console.log(logPrefix + " failed", error);
        //Check if any connection is held in for the conference
        const conferenceConnections = contact.getActiveConnections();
        if (conferenceConnections.length > 0) {
          //resume this connection
          conferenceConnections.forEach((connection: any) => {
            if (connection.isOnHold()) {
              connection.resume({
                success: function () {
                  console.log(logPrefix + "Successfully resumed connection");
                },
                failure: function (error: any) {
                  console.log(logPrefix + "Error in resuming connection", error);
                },
              });
            }
          });
        }
        Emitter.emit(TELEMETRY_ERROR, {
          type: "notif",
          chan: TELEMETRY_ERROR,
          body: {
            type: API_CALL_MERGE,
            error: error,
            info: logPrefix + " Failed to merge connections into conference",
          },
        });
      },
    });
  } catch (e) {
    console.log(logPrefix + " Unexpected error during merge", e);
  }
}

export function muteUnmute(ev: any) {
  const { mute } = ev.body;
  const logPrefix = lp + "muteUnmute: ";

  try {
    // Check if we're in a barge-in call
    const currentCall = connectSession.getCall();
    const currentConference = connectSession.getConferenceCall();
    const isBargeIn = currentCall?.isBarge;

    if (isBargeIn || currentConference?.id) {
      // For call, barge-in amd conference , use global agent mute/unmute
      console.log(
        logPrefix + `${mute ? "Muting" : "Unmuting"} barge-in globally`
      );
      if (mute) {
        updateConferenceAndSend(
          CONTACT_EVENT_STATE_MAP.MUTETOGGLE,
          { contactId: currentConference?.id },
          { muted: true }
        );
      } else {
        updateConferenceAndSend(
          CONTACT_EVENT_STATE_MAP.MUTETOGGLE,
          { contactId: currentConference?.id },
          { muted: false }
        );
      }
      return;
    }
  } catch (error) {
    console.error(logPrefix + "Error in mute/unmute operation", error);
  }
}

export function muteUnmuteConferenceParticipant(ev: {
  body: { conversationId: string; connectionId: string };
}) {
  const { conversationId, connectionId } = ev.body; //type can be "customer" or "agent"
  const logPrefix = lp + "muteUnmuteConferenceParticipant: ";

  // Validate active contact using reusable utility
  const contact = validateActiveContact(true, logPrefix);
  if (!contact) {
    emitTelemetryError(
      API_CALL_CONFERENCE_PARTICIPANT_MUTE,
      "No active call found",
      logPrefix + "No active call to mute/unmute conference participant"
    );
    return;
  } else {
    console.log(
      logPrefix + "Active call found to mute/unmute conference participant"
    );
  }

  try {
    // Get conference participants using reusable utility
    const activeConnections = contact.getActiveConnections();

    if (!activeConnections.length) {
      console.log(logPrefix + "No conference participants found");
      return;
    }

    // Find the specific connection that matches the connectionId
    const targetConnection = activeConnections.find((conn: any) => {
      try {
        return conn.getConnectionId && conn.getConnectionId() === connectionId;
      } catch (e) {
        console.log(logPrefix + "Error getting connection ID", e);
        return false;
      }
    });

    if (!targetConnection) {
      console.log(
        logPrefix +
        `No conference participant found with connectionId: ${connectionId}`
      );
      emitTelemetryError(
        API_CALL_CONFERENCE_PARTICIPANT_MUTE,
        `Connection not found with ID: ${connectionId}`,
        logPrefix +
        `No conference participant found with connectionId: ${connectionId}`,
        { connectionId, conversationId }
      );
      return;
    }

    // Call muteParticipant on the specific connection
    const isMuted = targetConnection.isMute();
    const muteAction = !isMuted
      ? targetConnection.muteParticipant
      : targetConnection.unmuteParticipant;

    if (typeof muteAction !== "function") {
      console.log(
        logPrefix +
        `${!isMuted ? "muteParticipant" : "unmuteParticipant"
        } method not available on connection`
      );
      emitTelemetryError(
        API_CALL_CONFERENCE_PARTICIPANT_MUTE,
        `${!isMuted ? "muteParticipant" : "unmuteParticipant"
        } method not available`,
        logPrefix +
        `${!isMuted ? "muteParticipant" : "unmuteParticipant"
        } method not available on connection`,
        { connectionId, conversationId }
      );
      return;
    }

    muteAction.call(targetConnection, {
      success: function () {
        console.log(
          logPrefix +
          `Conference participant ${isMuted ? "muted" : "unmuted"
          } successfully for connectionId: ${connectionId}`
        );

        emitTelemetryInfo(
          API_CALL_CONFERENCE_PARTICIPANT_MUTE,
          logPrefix +
          `Conference participant ${isMuted ? "muted" : "unmuted"
          } successfully`,
          logPrefix +
          `${isMuted ? "mute" : "unmute"} conference participant success`,
          { connectionId, conversationId, isMuted }
        );
      },
      failure: function (error: any) {
        console.log(
          logPrefix +
          `Failed to ${isMuted ? "mute" : "unmute"
          } conference participant for connectionId: ${connectionId}`,
          error
        );
        emitTelemetryError(
          API_CALL_CONFERENCE_PARTICIPANT_MUTE,
          error,
          logPrefix +
          `Failed to ${isMuted ? "mute" : "unmute"} conference participant`,
          { connectionId, conversationId, isMuted }
        );
      },
    });
  } catch (e) {
    console.log(
      logPrefix + `Error in muting/unmuting conference participant`,
      e
    );
    emitTelemetryError(
      API_CALL_CONFERENCE_PARTICIPANT_MUTE,
      e,
      logPrefix + `Error in muting/unmuting conference participant`,
      { connectionId, conversationId, isMuted: false }
    );
  }
}

// Handle conference hold/resume function
function manageConferenceParticipants(action: "hold" | "resume") {
  const logPrefix = lp + `${action}Conference `;

  // Validate active contact using reusable utility
  const contact = validateActiveContact(true, logPrefix);
  if (!contact) {
    const telemetryType =
      action === "hold" ? API_CALL_CONFERENCE_HOLD : API_CALL_CONFERENCE_PLAY;
    emitTelemetryError(
      telemetryType,
      "No active call found",
      logPrefix + `No active call to ${action}`
    );
    return;
  }

  try {
    // Get conference participants using reusable utility
    const thirdPartyConnections = getThirdPartyConnections(contact, logPrefix);

    if (!thirdPartyConnections.length) {
      console.log(logPrefix + "No conference participants found");
      return;
    }

    const telemetryType =
      action === "hold" ? API_CALL_CONFERENCE_HOLD : API_CALL_CONFERENCE_PLAY;

    // Process ALL conference participants, not just the first
    thirdPartyConnections.forEach((conn: any, index: number) => {
      const connectionAction = action === "hold" ? conn.hold : conn.resume;

      connectionAction.call(conn, {
        success: function () {
          console.log(
            logPrefix +
            `Conference participant ${index + 1} ${action}ed successfully`
          );

          // Update conference state only after the last participant
          if (index === thirdPartyConnections.length - 1) {
            emitTelemetryInfo(
              telemetryType,
              logPrefix +
              `All conference participants ${action}ed successfully`,
              logPrefix + `${action} conference success`,
              { participantCount: thirdPartyConnections.length }
            );
          }
        },
        failure: function (error: any) {
          console.log(
            logPrefix +
            `Failed to ${action} conference participant ${index + 1}`,
            error
          );
          emitTelemetryError(
            telemetryType,
            error,
            logPrefix +
            `Failed to ${action} conference participant ${index + 1}`
          );
        },
      });
    });
  } catch (e) {
    console.log(logPrefix + `Error in ${action}ing conference`, e);
    const telemetryType =
      action === "hold" ? API_CALL_CONFERENCE_HOLD : API_CALL_CONFERENCE_PLAY;
    emitTelemetryError(
      telemetryType,
      e,
      logPrefix + `Error in ${action}ing conference`
    );
  }
}

export function holdConference() {
  manageConferenceParticipants("hold");
}

export function playConference() {
  manageConferenceParticipants("resume");
}

// Disconnect agent from call/conference while preserving other participants
export function disconnectAgentFromCall(ev: any) {
  const logPrefix = lp + "disconnectAgentFromCall: ";
  const { contactId, reason = "agent_disconnect" } = ev.body || {};

  console.log(
    logPrefix + `Disconnecting agent from ${contactId}, reason: ${reason}`
  );

  try {
    const contact = getAgentContactById(false, contactId);

    if (contact && contact.getAgentConnection) {
      const agentConnection = contact.getAgentConnection();

      if (agentConnection) {
        console.log(logPrefix + "Destroying agent connection only");
        agentConnection.destroy({
          success: function () {
            console.log(logPrefix + "Agent connection destroyed successfully");
            emitTelemetryInfo(
              "AGENT_DISCONNECT",
              logPrefix + "Agent disconnected successfully",
              logPrefix + "agent disconnect success",
              { contactId, reason }
            );
          },
          failure: function (err: any) {
            console.log(logPrefix + "Failed to destroy agent connection", err);
            emitTelemetryError(
              "AGENT_DISCONNECT",
              err,
              logPrefix + "Failed to destroy agent connection"
            );
          },
        });
      } else {
        console.log(logPrefix + "No agent connection found");
        emitTelemetryError(
          "AGENT_DISCONNECT",
          "No agent connection found",
          logPrefix + "No agent connection found"
        );
      }
    } else {
      console.log(logPrefix + "No contact found for agent disconnect");
      emitTelemetryError(
        "AGENT_DISCONNECT",
        "No contact found",
        logPrefix + "No contact found for agent disconnect"
      );
    }
  } catch (error) {
    console.error(logPrefix + "Error in disconnectAgentFromCall", error);
    emitTelemetryError(
      "AGENT_DISCONNECT",
      error,
      logPrefix + "Error in disconnectAgentFromCall"
    );
  }
}

export function transferCallToQuickConnects(ev: any) {
  const logPrefix =
    lp + "transferCallToQuickConnects contactId: " + ev.body?.conversationId;
  if (!ev?.body?.transferEndpoint) {
    console.log(logPrefix + " No transfer endpoint provided");
    return;
  }

  // Validate active call connection to transfer
  const callConnection = getAgentContactById(true);
  if (!callConnection) {
    console.log(logPrefix + " No active call to transfer");
    return;
  }

  // Build transfer endpoint object
  const transferEndpoint = {
    agentLogin: null,
    endpointId: ev.body.transferEndpoint.arn,
    endpointARN: ev.body.transferEndpoint.arn,
    phoneNumber: null,
    name: ev.body.transferEndpoint.name,
    queue: null,
    type: ev.body.transferEndpoint.quick_connect_type.toLowerCase(),
  } as unknown as connect.EndpointType.QUEUE;

  try {
    // Initiate transfer by adding connection
    callConnection.addConnection(transferEndpoint, {
      success: function (data: any) {
        console.log(
          logPrefix + " Transfer connection established successfully",
          data
        );

        // Disconnect agent after successful transfer
        const agentConnection = callConnection.getAgentConnection();
        if (agentConnection) {
          agentConnection.destroy({
            success: function () {
              console.log(
                logPrefix + " Agent disconnected successfully after transfer"
              );
              Emitter.emit(TELEMETRY_ERROR, {
                type: "notif",
                chan: TELEMETRY_INFO,
                body: {
                  type: API_CALL_TRANSFER,
                  info:
                    logPrefix +
                    " Call transferred successfully to " +
                    ev.body.transferEndpoint.name,
                  message: logPrefix + " call transfer success",
                  conversationId: ev.body.conversationId,
                  transferEndpoint: ev.body.transferEndpoint.name,
                },
              });
            },
            failure: function (err: any) {
              console.log(
                logPrefix + " Failed to disconnect agent after transfer",
                err
              );
              Emitter.emit(TELEMETRY_ERROR, {
                type: "notif",
                chan: TELEMETRY_ERROR,
                body: {
                  type: API_CALL_TRANSFER,
                  error: err,
                  info:
                    logPrefix + " Failed to disconnect agent after transfer",
                  conversationId: ev.body.conversationId,
                },
              });
            },
          });
        }
      },
      failure: function (error: any) {
        console.log(logPrefix + " Call transfer failed", error);
      },
    });
  } catch (e) {
    console.log(logPrefix + " Unexpected error during transfer", e);
  }
}

export function senfDTMF(event: any) {
  const logPrefix = lp + "senfDTMF contactId: " + event.body.conversationId;
  let callConnection = getAgentContactById(true).getAgentConnection();
  callConnection.sendDigits(event.body?.digits?.toString(), {
    success: function (resp: any) {
      console.log(logPrefix + "DTMF sent successfully", resp);
    },
    failure: function (error: any) {
      console.log(logPrefix + "Error in sending DTMF", error);
      Emitter.emit(TELEMETRY_ERROR, {
        type: "notif",
        chan: TELEMETRY_ERROR,
        body: {
          type: API_CALL_SEND_DTMF,
          error: error,
          message: `${logPrefix} Error in sending DTMF`,
        },
      });
    },
  });
}

// Helper function to find connection ID by phone number
export function getConnectionIdByPhoneNumber(phoneNumber: string) {
  const logPrefix =
    lp + "getConnectionIdByPhoneNumber phoneNumber: " + phoneNumber;
  try {
    const connections = getActiveConnections();
    console.log(logPrefix + `Total connections found: ${connections.length}`);
    // Log all connections for debugging
    connections.forEach((conn: any, index: number) => {
      console.log(logPrefix + `Connection ${index}:`, {
        id: conn.connectionId,
        phone: conn.phoneNumber,
      });
    });
    // Find connection that matches the phone number
    const matchingConnection = connections.find((conn: any) => {
      const phoneMatch = conn.phoneNumber === phoneNumber;
      const notAgent = !conn.isAgent;
      return phoneMatch && notAgent;
    });
    if (matchingConnection) {
      return matchingConnection.connectionId;
    }
    console.log(
      logPrefix + `No connection found for phone number: ${phoneNumber}`
    );
    storeRTK.dispatch(resetConference());
    return null;
  } catch (error) {
    console.log(logPrefix + "Error finding connection by phone number", error);
    storeRTK.dispatch(resetConference());
    return null;
  }
}

// Helper function to get all active connections with detailed info
export function getActiveConnections() {
  const logPrefix = lp + "getActiveConnections ";

  try {
    const contact = getAgentContactById(true);
    if (!contact) return [];

    let allConnections: any[] = [];
    try {
      if (typeof (contact as any).getConnections === "function") {
        allConnections = (contact as any).getConnections();
      }
    } catch (e) {
      return [];
    }

    // Map connections with detailed info
    return allConnections
      .map((conn: any) => {
        try {
          const connectionId =
            conn.connectionId ||
            (conn.getConnectionId && conn.getConnectionId());
          const endpoint = conn.getEndpoint && conn.getEndpoint();
          const connectionType = conn.getType && conn.getType();
          const isConnected = conn.isConnected && conn.isConnected();
          const status = conn.getStatus && conn.getStatus();
          const isOnHold = conn.isOnHold && conn.isOnHold();

          return {
            connectionId,
            phoneNumber: endpoint?.phoneNumber,
            type: String(connectionType).toLowerCase(),
            isConnected,
            status: status?.type,
            isOnHold,
            isAgent: String(connectionType).toLowerCase() === "agent",
            isInitial: conn === contact.getActiveInitialConnection(),
            isThirdParty:
              String(connectionType).toLowerCase() !== "agent" &&
              conn !== contact.getActiveInitialConnection(),
            connection: conn,
          };
        } catch (e) {
          return null;
        }
      })
      .filter((conn) => conn !== null);
  } catch (error) {
    console.log(logPrefix + "Error getting active connections", error);
    return [];
  }
}

// Function to identify which connection to remove after merge
export function identifyConnectionToRemoveAfterMerge() {
  const logPrefix = lp + "identifyConnectionToRemoveAfterMerge ";

  try {
    const connections = getActiveConnections();
    console.log(logPrefix + "All connections:", connections);

    // Filter out agent connections (never remove agent)
    const nonAgentConnections = connections.filter((conn) => !conn.isAgent);

    // Separate initial and third-party connections
    const initialConnections = nonAgentConnections.filter(
      (conn) => conn.isInitial
    );
    const thirdPartyConnections = nonAgentConnections.filter(
      (conn) => conn.isThirdParty
    );

    return {
      allConnections: connections,
      nonAgentConnections,
      initialConnections,
      thirdPartyConnections,
      agentConnection: connections.find((conn) => conn.isAgent),
    };
  } catch (error) {
    console.log(logPrefix + "Error identifying connections", error);
    return {
      allConnections: [],
      nonAgentConnections: [],
      initialConnections: [],
      thirdPartyConnections: [],
      agentConnection: null,
    };
  }
}

// Main function to remove specific connection by ID
export function removeSpecificConnection(ev: any) {
  const logPrefix = lp + "removeSpecificConnection ";
  const { connectionId } = ev.body || {};

  // Get connection analysis
  const connectionInfo = identifyConnectionToRemoveAfterMerge();

  // Find the specific connection by ID
  const targetConnection = connectionInfo.allConnections.find((conn: any) => {
    return conn.connectionId === connectionId;
  });

  // Determine which state to clear based on phone number matching
  const currentState = storeRTK.getState();
  const conference = currentState.conference;
  const call = currentState.call;

  if (targetConnection) {
    // Get phone numbers from states
    const callPhoneNumber =
      call?.customer?.phoneNumber || call?.customer?.displayName;
    const conferencePhoneNumber =
      conference?.customer?.phoneNumber || conference?.customer?.displayName;
    const removedPhoneNumber = targetConnection.phoneNumber;
    // Remove the connection
    setTimeout(() => {
      targetConnection.connection.destroy({
        success: function () {
          // Trigger appropriate ENDED state events based on connection type
          if (
            removedPhoneNumber === callPhoneNumber ||
            targetConnection.isInitial
          ) {
            console.log(
              logPrefix +
              "Manually triggering ENDED state event for removed active call"
            );
            updateCallAndSend(CONTACT_EVENT_STATE_MAP.ENDED, {
              contactId: call?.id,
              getContactId: () => call?.id,
            });
          } else if (
            removedPhoneNumber === conferencePhoneNumber ||
            targetConnection.isThirdParty
          ) {
            updateConferenceAndSend(CONTACT_EVENT_STATE_MAP.ENDED, {
              contactId: conference?.id || connectionId,
              getContactId: () => conference?.id || connectionId,
            });
          }
          // Emit telemetry
          Emitter.emit(TELEMETRY_ERROR, {
            type: "notif",
            chan: TELEMETRY_INFO,
            body: {
              type: API_CALL_HANGUP,
              message: logPrefix + `Connection ${connectionId} removed`,
              disconnectReason: "Agent Disconnect Specific Connection",
            },
          });
        },
        failure: function (err: any) {
          console.log(
            logPrefix + `Failed to remove connection ${connectionId}`,
            err
          );

          // If removal failed but we need to handle state, still trigger the ENDED event
          if (
            removedPhoneNumber === callPhoneNumber ||
            targetConnection.isInitial
          ) {
            updateCallAndSend(CONTACT_EVENT_STATE_MAP.ENDED, {
              contactId: call?.id,
              getContactId: () => call?.id,
            });
          } else if (
            removedPhoneNumber === conferencePhoneNumber ||
            targetConnection.isThirdParty
          ) {
            updateConferenceAndSend(CONTACT_EVENT_STATE_MAP.ENDED, {
              contactId: conference?.id || connectionId,
              getContactId: () => conference?.id || connectionId,
            });
          }

          Emitter.emit(TELEMETRY_ERROR, {
            type: "notif",
            chan: TELEMETRY_ERROR,
            body: {
              type: API_CALL_HANGUP,
              error: err,
              message:
                logPrefix + `Failed to remove connection ${connectionId}`,
            },
          });
        },
      });
    }, 500);
  }
}

// Helper function to get global mute state from Redux store
export function getAgentGlobalMuteState(): boolean {
  const logPrefix = lp + "getAgentMuteStateFromContact: ";

  try {
    if (!connectSession.agent) {
      console.log(logPrefix + "No agent session available");
      return false;
    }

    const contacts = connectSession.agent.getContacts(
      connect.ContactType.VOICE
    );
    if (contacts && contacts.length > 0) {
      const contact = contacts[0];
      const agentConnection = contact.getAgentConnection();

      if (agentConnection && typeof agentConnection.isMute === "function") {
        const isMuted = agentConnection.isMute();
        console.log(logPrefix + `Agent connection mute state: ${isMuted}`);
        return isMuted;
      }
    }

    console.log(
      logPrefix + "No active voice contact or mute method not available"
    );
    return false;
  } catch (error) {
    console.error(
      logPrefix + "Error getting agent mute state from contact",
      error
    );
    return false;
  }
}

// Global agent mute/unmute function
export function globalAgentMute(ev: any) {
  const { mute } = ev.body;
  const logPrefix = lp + "globalAgentMute: ";

  try {
    if (!connectSession.agent) {
      console.log(logPrefix + "No agent session available");
      emitTelemetryError(
        API_AGENT_GLOBAL_MUTE,
        "No agent session available",
        logPrefix + "No agent session available for global mute"
      );
      return;
    }

    if (mute) {
      console.log(logPrefix + "Globally muting agent");
      connectSession.agent.mute();
    } else {
      console.log(logPrefix + "Globally unmuting agent");
      connectSession.agent.unmute();
    }

    // Update Redux state
    storeRTK.dispatch(setAgentGlobalMute({ isAgentGloballyMuted: mute }));

    emitTelemetryInfo(
      API_AGENT_GLOBAL_MUTE,
      logPrefix + `Agent globally ${mute ? "muted" : "unmuted"}`,
      logPrefix + `Global agent ${mute ? "mute" : "unmute"} success`,
      { muted: mute }
    );
  } catch (error) {
    console.error(
      logPrefix + "Error in global agent mute/unmute operation",
      error
    );
    emitTelemetryError(
      API_AGENT_GLOBAL_MUTE,
      error,
      logPrefix + "Error in global agent mute/unmute operation",
      { muted: mute }
    );
  }
}

export function holdOrResumeAgentConnection(ev: any) {
  const { conversationId, isOnHold, connectionId } = ev.body || {};
  const logPrefix = lp + "holdOrResumeAgentConnection: ";
  console.log(
    logPrefix +
    `Toggle ${isOnHold ? "resume" : "hold"} for conversationId=${conversationId} connectionId=${connectionId || "agent"}`
  );
  try {
    // Prefer locating contact by explicit conversationId; fallback to current active voice contact
    let contact: any = null;
    if (conversationId) {
      contact = getAgentContactById(false, conversationId);
    }
    if (!contact) {
      contact = getAgentContactById(true);
    }
    if (!contact) {
      console.log(logPrefix + "No active contact found to hold/resume");
      return;
    }

    // Determine the specific connection to operate on
    let targetConn: any = null;

    // If a specific connectionId is provided, attempt to find that connection
    if (connectionId && typeof (contact as any).getConnections === "function") {
      try {
        const all = (contact as any).getConnections();
        targetConn = all.find((c: any) => {
          try {
            return (
              (c.getConnectionId && c.getConnectionId() === connectionId) ||
              c.connectionId === connectionId
            );
          } catch {
            return false;
          }
        });
      } catch {}
    }

    // Otherwise, use the agent connection first (self leg), then fall back to initial connection
    if (!targetConn && typeof contact.getAgentConnection === "function") {
      try {
        targetConn = contact.getAgentConnection();
      } catch {}
    }
    if (!targetConn && typeof contact.getActiveInitialConnection === "function") {
      try {
        targetConn = contact.getActiveInitialConnection();
      } catch {}
    }

    if (!targetConn) {
      console.log(logPrefix + "No target connection found to hold/resume");
      return;
    }

    if (isOnHold) {
      // Resume the target connection
      if (typeof targetConn.resume === "function") {
        targetConn.resume({
          success: function () {
            console.log(logPrefix + "Successfully resumed target connection");
          },
          failure: function (error: any) {
            console.log(logPrefix + "Error in resuming target connection", error);
          },
        });
      } else {
        console.log(logPrefix + "resume not supported on target connection");
      }
    } else {
      // Hold the target connection (avoid duplicate hold)
      if (typeof targetConn.hold === "function") {
        try {
          if (typeof targetConn.isOnHold === "function" && targetConn.isOnHold()) {
            console.log(logPrefix + "Target connection already on hold");
            return;
          }
        } catch {}
        targetConn.hold({
          success: function () {
            console.log(logPrefix + "Successfully held target connection");
          },
          failure: function (error: any) {
            console.log(logPrefix + "Error in holding target connection", error);
          },
        });
      } else {
        console.log(logPrefix + "hold not supported on target connection");
      }
    }
  } catch (e) {
    console.log(logPrefix + "Unexpected error in hold/resume operation", e);
  }
}

export const registerApiCallEvents = () => {
  Emitter.on(API_CALL_HANGUP, disconnectCallContact);
  Emitter.on(API_CALL_DIAL, initOutBoundCall);
  Emitter.on(API_CALL_MUTE, muteUnmute);
  Emitter.on(API_CALL_HOLD, updateCall);
  Emitter.on(API_CALL_PLAY, updateCall);
  Emitter.on(API_CALL_ANSWER, acceptCallContact);
  Emitter.on(API_CALL_REJECT, rejectCallContact);
  Emitter.on(API_CALL_SEND_DTMF, senfDTMF);
  Emitter.on(API_CALL_TRANSFER, transferCallToQuickConnects);
  Emitter.on(API_CALL_ADD_CONNECTION, addConnectionToCall);
  Emitter.on(API_CALL_ADD_QUEUE_CONNECTION, addQueueConnectionToCall);
  Emitter.on(API_CALL_MERGE, mergeConnections);
  Emitter.on(API_CALL_CONFERENCE_HOLD, holdConference);
  Emitter.on(API_CALL_CONFERENCE_PLAY, playConference);
  Emitter.on(API_CALL_REMOVE_CONNECTION, removeSpecificConnection);
  Emitter.on(API_CALL_DISCONNECT_AGENT, disconnectAgentFromCall);
  Emitter.on(API_CALL_BARGE, escalateMonitorToBarge);
  Emitter.on(
    API_CALL_CONFERENCE_PARTICIPANT_MUTE,
    muteUnmuteConferenceParticipant
  );
  Emitter.on(API_AGENT_GLOBAL_MUTE, globalAgentMute);
  Emitter.on(API_CALL_HOLD_OR_RESUME_AGENT_CONNECTION, holdOrResumeAgentConnection);
};
